import React, {
	useCallback, useMemo, useRef, useState, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FullCalendar, { DatesSetArg, EventClickArg, EventContentArg } from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import classNames from 'classnames';
import { isEqual } from 'underscore';
import TellyCard from '../../../images/telly-card.png';
import { Meeting } from '../../../stores/reducers/session';
import { cardBackground } from '../../../utils/utils';
import SlidingMenu from '../../general-components/sliding-menu';
import Badges from '../../general-components/badges';
import AvatarBubbleRow from '../../general-components/avatar-bubble-row';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import useHome from '../../../hooks/use-home';
import useJoin from '../../../hooks/use-join';
import WaitingIndicator from '../../general-components/waiting-indicator';
import { GreenroomState } from '../../../stores/types/types';
import { setAwaitingProducer } from '../../../stores/actions/session';
import { setSelectedCalendarOption } from '../../../stores/actions/user-interface';
import EventMenu from '../../general-components/event-menu';
import { APP } from '../../../stores/session-status';
import useMediaDevices from '../../../hooks/use-media-devices';

interface Props {
	calendarType: string,
	showSessions: Meeting[]
}

interface SlidingMenuOption {
	label: string,
	data: {
		month: number,
		year: number,
		day: number
	}
}

interface CalendarEvent {
	title: string,
	date: Date,
	start: Date,
	end: Date,
	customData: {
		color: string,
		identifier: string,
		backgroundStyle: {
			backgroundImage: string,
			backgroundColor?: string,
			backgroundSize?: string
		}
		start_time: number,
		end_time: number,
		show_session_type_id: number,
		show_session_type: string,
		invites: any,
		title: string,
		isSetUp: boolean
	}
}

interface PopData {
	top: string,
	left: string,
	pos: string,
	title: string,
	backgroundStyle: any,
	identifier: string,
	start_time: number,
	end_time: number,
	show_session_type_id: number,
	show_session_type: string,
	invites: any,
	time: string,
	isSetUp: boolean
}

const MONTH_ARR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function numSort(a: any, b: any) { return a - b; }

/* there's a lot of shitty hacks in here but at least it's all one component and not events 2.5 comments */

export default function CalendarView(props: Props) {
	const joinRequest = useSelector((state: GreenroomState) => state.InvitationsReducer.joinRequest);
	const awaitingProducer = useSelector((state: GreenroomState) => state.SessionReducer.awaitingProducer);
	const appSignedInWith = useSelector((state: GreenroomState) => state.SessionReducer.appSignedInWith);
	const selectedCalendarOption = useSelector((state: GreenroomState) => state.UserInterfaceReducer.selectedCalendarOption);
	const selectedChannel = useSelector((state: GreenroomState) => state.HomeReducer.selectedChannel);

	const dispatch = useDispatch();

	const { showSessions, calendarType } = props;

	const calendarRef = useRef<any>(null);
	const popoverRef = useRef<HTMLInputElement>(null);

	const [popIsOpen, setPopIsOpen] = useState(false);
	const [extraEventsOpen, setExtraEventsOpen] = useState(false);
	const [maxEventsADay, setMaxEventsADay] = useState(2);
	const [timeGridStartTime, setTimeGridStartTime] = useState('00:00');
	const [currentCalendarType, setCurrentCalendarType] = useState<string>(calendarType);

	const { handleEnterShowDetails, showDetailsButtonDisabled } = useHome();
	const { joinGreenroom } = useJoin();

	const [popData, setPopData] = useState<PopData>({
		top: '0%',
		left: '0%',
		pos: 'right',
		title: '',
		backgroundStyle: {},
		identifier: '',
		time: '',
		start_time: 0,
		end_time: 0,
		show_session_type_id: 0,
		show_session_type: 'Broadcast',
		invites: [],
		isSetUp: false,
	});

	const [extraEventsData, setExtraEventsData] = useState({
		top: '0%',
		left: '0%',
		dateRow: '',
		events: [],
	});

	useEffect(() => {
		if (calendarRef.current) {
			const calendarApi = calendarRef.current.getApi();
			calendarApi.changeView(calendarType);

			if (selectedCalendarOption) {
				calendarApi.gotoDate(new Date(selectedCalendarOption.data.year, selectedCalendarOption.data.month, selectedCalendarOption.data.day));
			}

			removeUnusedLeadingHours();
		}
	}, [calendarType]);

	useEffect(() => {
		if (awaitingProducer && !joinRequest) {
			dispatch(setAwaitingProducer(false));
		}
	}, [awaitingProducer, dispatch, joinRequest]);

	const removeUnusedLeadingHours = () => {
		// we want to remove the first hours of a day if there are no events in those hours
		if (calendarRef && calendarRef.current) {
			const calendarApi = calendarRef.current.getApi();

			const events = calendarApi.getEvents();
			const viewStart = new Date(calendarApi.view.activeStart).valueOf();
			const viewEnd = new Date(calendarApi.view.activeEnd).valueOf();

			const startHoursOfEventsInView = events.map((ev: any) => {
				const evStart = new Date(ev.start).valueOf();
				if (evStart >= viewStart && evStart <= viewEnd) return new Date(evStart).getHours();
			}).filter(Boolean).sort(numSort);

			// if no events, set start time to midnight
			let earliestStartTime = startHoursOfEventsInView.length > 0 ? startHoursOfEventsInView[0] : 0;

			// start 1 hour before event for some space
			if (earliestStartTime !== 0) earliestStartTime--;

			// if earliest event is after 11am just show 10am as start
			if (earliestStartTime > 11) earliestStartTime = 10;

			setTimeGridStartTime(`${earliestStartTime}:00`);
		}
	};

	const calendarEvents = useMemo(() => {
		const events: CalendarEvent[] = [];

		showSessions.map((event: any) => {
			const start = new Date(event.start_time);
			const end = new Date(event.end_time);
			let color = 'green';

			switch (event.show_session_type.toLowerCase()) {
				case 'recording':
					color = 'orange';
					break;
				case 'rehearsal':
					color = 'purple';
					break;
				case 'tech check':
					color = 'gray';
					break;
			}

			let identifier;
			if (event.events_session_uuid) {
				// Events
				identifier = event.events_session_uuid;
			} else if (event.live_chat) {
				// Events Legacy
				identifier = event.live_chat;
			} else if (event.meeting) {
				// Standalone GR
				identifier = event.meeting;
			}

			events.push({
				title: event.title,
				date: start,
				start,
				end,
				customData: {
					color,
					backgroundStyle: cardBackground(event, TellyCard, true, true),
					identifier,
					start_time: event.start_time ?? 0,
					end_time: event.end_time ?? 0,
					show_session_type: event.show_session_type ?? 'Broadcast',
					show_session_type_id: event.show_session_type_id ?? 0,
					invites: event.invites && event.invites.length ? event.invites : [],
					title: event.title ?? '',
					isSetUp: !!event.meeting,
				},
			});
		});

		return events;
	}, [showSessions]);

	const slidingMenuOptions = useMemo(() => {
		const newOptionsArr: SlidingMenuOption[] = [];
		let i = 0;
		let maxOptions = 0;
		let label = '';
		const writingDate = new Date();

		if (currentCalendarType === 'dayGridMonth') {
			maxOptions = 12;
			writingDate.setDate(1);

			for (i; i < maxOptions; i++) {
				const data = { year: writingDate.getFullYear(), month: writingDate.getMonth(), day: writingDate.getDate() };
				label = writingDate.toLocaleString([], { month: 'short', year: 'numeric' });

				newOptionsArr.push({ label, data });
				writingDate.setMonth(writingDate.getMonth() + 1);
			}
		} else if (currentCalendarType === 'timeGridWeek') {
			maxOptions = 52;

			// start week on mon preceeding current day (calendar starts on mon so sun is -6, sat is -5, etc)
			const weekDay = writingDate.getDay() === 0 ? 6 : writingDate.getDay() - 1;
			writingDate.setDate(writingDate.getDate() - weekDay);

			for (i; i < maxOptions; i++) {
				const data = { year: writingDate.getFullYear(), month: writingDate.getMonth(), day: writingDate.getDate() };

				const dateAtEndOfWeek = new Date(writingDate.valueOf() + 7 * 24 * 60 * 60 * 1000);

				let label1; let
					label2 = '';
				if (writingDate.getMonth() !== dateAtEndOfWeek.getMonth()) {
					label1 = writingDate.toLocaleString([], { day: 'numeric', month: 'short' });
					label2 = dateAtEndOfWeek.toLocaleString([], { day: 'numeric', month: 'short' });
				} else {
					label1 = `${MONTH_ARR[dateAtEndOfWeek.getMonth()]} ${writingDate.getDate()}`;
					label2 = `${dateAtEndOfWeek.getDate()}`;
				}

				label = `${label1} - ${label2}`;
				newOptionsArr.push({ label, data });
				writingDate.setDate(writingDate.getDate() + 7);
			}
		} else if (currentCalendarType === 'timeGridDay') {
			maxOptions = 365;

			for (i; i < maxOptions; i++) {
				const data = { year: writingDate.getFullYear(), month: writingDate.getMonth(), day: writingDate.getDate() };
				label = writingDate.toLocaleString([], { day: 'numeric', month: 'short', weekday: 'short' });

				newOptionsArr.push({ label, data });
				writingDate.setDate(writingDate.getDate() + 1);
			}
		}

		return newOptionsArr;
	}, [currentCalendarType]);

	useEffect(() => {
		if (!slidingMenuOptions.some((option: any) => isEqual(option, selectedCalendarOption))) {
			dispatch(setSelectedCalendarOption(slidingMenuOptions[0]));
		}
	}, [slidingMenuOptions]);

	const customDataToEventData = useCallback((customData: { [key: string]: any }) => {
		if (customData?.identifier) {
			const appIds = {
				events_session_uuid: '',
				live_chat: '',
				meeting: '',
				title: customData.title,
				channel: selectedChannel,
			};

			switch (appSignedInWith) {
				case APP.EVENTS:
					appIds.events_session_uuid = customData.identifier;
					break;
				case APP.EVENTS_LEGACY:
					appIds.live_chat = customData.identifier;
					break;
				case APP.GREENROOM:
					appIds.meeting = customData.identifier;
					break;
				default:
					break;
			}

			return { ...customData, ...appIds };
		}
	}, [appSignedInWith, selectedChannel]);

	const renderEventContent = (eventInfo: EventContentArg) => {
		const {
			identifier = '',
			 color = '',
			 start_time = 0,
			 end_time = 0,
			 invites = [],
			 show_session_type = 'Broadcast',
		} = eventInfo.event._def.extendedProps.customData;

		const broadcastLength = (new Date(end_time).getTime() - new Date(start_time).getTime()) / 60000;
		const extendedPropsToEventData = customDataToEventData(eventInfo.event._def.extendedProps.customData);

		if (currentCalendarType === 'dayGridMonth') {
			return (
				<div className={classNames('event-container month-view', { selected: identifier === popData.identifier })}>
					<span className={classNames('event-color-indicator', color)}></span>
					<span className="fc-cus-time">{eventInfo.timeText}</span>
					<span className="fc-cus-title">{eventInfo.event.title}</span>
				</div>
			);
		} if (currentCalendarType === 'timeGridWeek') {
			return (
				<div className={classNames('event-container week-view', { selected: identifier === popData.identifier }, broadcastLength <= 30 && 'half-card')}>
					<span className={classNames('event-color-indicator', color)}></span>
					{broadcastLength > 30 && <div className="fc-cus-time">{eventInfo.timeText}</div>}
					<div className="fc-cus-title">{eventInfo.event.title}</div>
				</div>
			);
		} if (currentCalendarType === 'timeGridDay') {
			if (broadcastLength >= 45) {
				return (
					<div className={classNames('event-container day-view', { selected: identifier === popData.identifier })}>
						<div>
							<span className={classNames('event-color-indicator', color)}></span>
							<div className="fc-cus-interactive">
								<div className="fc-cus-time">{eventInfo.timeText}</div>
								<div className="fc-cus-day-menu">
									{extendedPropsToEventData && (
										<EventMenu event={extendedPropsToEventData} />
									)}
								</div>
							</div>
							<div className="fc-cus-title">{eventInfo.event.title}</div>
						</div>
						<div className="fc-cus-day-bottom">
							<Badges
								start_time={start_time}
								end_time={end_time}
								show_session_type={show_session_type}
							/>
							{invites.length > 0 && <div className="fc-cus-day-bottom-divider">|</div>}
							<AvatarBubbleRow invites={invites} size={'thirtytwo'} alignEnd={true} />
							<div className="fc-cus-day-bottom-arrow" onClick={() => handleSetUp(identifier)}>
								<Icon name={ICONS.ARROW_RIGHT} color={COLORS.GRAY_PRESENTER} size={20} />
							</div>
						</div>
					</div>
				);
			} if (broadcastLength > 30) {
				return (
					<div className={classNames('event-container day-view half-card', { selected: identifier === popData.identifier })}>
						<div className="fc-cus-time-title-container">
							<span className={classNames('event-color-indicator', color)}></span>
							<div className="fc-cus-time">{eventInfo.timeText}</div>
							<div className="fc-cus-title">{eventInfo.event.title}</div>
						</div>
						<div className="fc-cus-day-bottom-arrow" onClick={() => handleSetUp(identifier)}>
							<Icon name={ICONS.ARROW_RIGHT} color={COLORS.GRAY_PRESENTER} size={20} />
						</div>
					</div>
				);
			}
			return (
					<div className={classNames('event-container day-view half-card', { selected: identifier === popData.identifier })}>
						<div className="fc-cus-time-title-container single-row">
							<span className={classNames('event-color-indicator', color)}></span>
							<div className="fc-cus-title">{eventInfo.event.title}</div>
						</div>
						<div className="fc-cus-day-bottom-arrow" onClick={() => handleSetUp(identifier)}>
							<Icon name={ICONS.ARROW_RIGHT} color={COLORS.GRAY_PRESENTER} size={20} />
						</div>
					</div>
			);
		}
	};

	const showMoreEvents = (eventClickInfo: any) => {
		// currently not used in favor of expanding the amount of events visible on a day
		const events: any = [];

		eventClickInfo.allSegs.map((seg: any) => {
			const { customData } = seg.event._def.extendedProps;
			events.push(customData);
		});

		const calendarEl = document.getElementById('fc-calendar-plugin');

		if (calendarEl) {
			const rect = calendarEl.getBoundingClientRect();

			const offset = {
				top: rect.top + window.scrollY,
				left: rect.left + window.scrollX,
			};

			const h = eventClickInfo.jsEvent.pageY - offset.top + 350;
			const w = eventClickInfo.jsEvent.pageX - offset.left + 20;
			const dateRow = new Date(eventClickInfo.date).toLocaleString([], { weekday: 'short', month: 'short', day: '2-digit' });

			setExtraEventsData({
				top: `${h}px`,
				left: `${w}px`,
				dateRow,
				events,
			});

			setExtraEventsOpen(true);
		}
	};

	const eventClicked = (eventClickInfo: EventClickArg) => {
		// opens a popover modal when an event is clicked in the week/month view
		if (currentCalendarType !== 'timeGridDay' && popoverRef.current && eventClickInfo.event.start && eventClickInfo.event.end) {
			// set in calendarEvents usememo
			const {
				backgroundStyle = '',
				 identifier = '',
				 start_time = 0,
				 end_time = 0,
				 show_session_type_id = 0,
				 show_session_type = 'Broadcast',
				 invites = [],
				 isSetUp = false,
			} = eventClickInfo.event._def.extendedProps.customData;
			const weekDay = new Date(eventClickInfo.event.start).getDay();
			const timeLabel1 = new Date(eventClickInfo.event.start).toLocaleString([], { hour: 'numeric', minute: '2-digit' });
			const timeLabel2 = new Date(eventClickInfo.event.end).toLocaleString([], { hour: 'numeric', minute: '2-digit' });
			const popoverHeight = popoverRef.current.offsetHeight;
			const popoverWidth = popoverRef.current.offsetWidth;
			const popoverGap = 20; // breathing room between click x coordinate and edge of popover
			let top = eventClickInfo.jsEvent.pageY - window.scrollY - popoverHeight / 2;
			let left = eventClickInfo.jsEvent.pageX - window.scrollX + popoverGap;
			let pos = 'right';

			// clamp to top
			if (top < 0) top = 0;
			// clamp to bottom
			if (top > window.innerHeight - popoverHeight) top = window.innerHeight - popoverHeight;
			// if the weekday is fri/sat(5/6) sun(0) we might not have enough space to show on the right, so put the modal on the left of the event
			if (weekDay > 4 || weekDay === 0) {
				left = eventClickInfo.jsEvent.pageX - window.scrollX - popoverWidth - popoverGap;
				pos = 'left';
			}

			setPopData({
				top: `${top}px`,
				left: `${left}px`,
				pos,
				title: eventClickInfo.event.title,
				time: `${timeLabel1} - ${timeLabel2}`,
				backgroundStyle,
				identifier,
				start_time,
				end_time,
				show_session_type_id,
				show_session_type,
				invites,
				isSetUp,
			});

			setPopIsOpen(true);
		}
	};

	const dateClicked = (e: any) => {
	};

	useEffect(() => {
		if (popIsOpen) window.addEventListener('mousedown', closeMonthPopOver);

		if (extraEventsOpen) window.addEventListener('mousedown', closeExtraEventsPopover);

		return () => {
			window.removeEventListener('mousedown', closeMonthPopOver);
			window.removeEventListener('mousedown', closeExtraEventsPopover);
			dispatch(setAwaitingProducer(false));
		};
	}, [popIsOpen, extraEventsOpen, dispatch]);

	function closeMonthPopOver(e: any) {
		// if (!popIsOpen) return;
		const path = e.composedPath();
		const len = path.length;
		let keepOpen = false;

		for (let i = 0; i < len; ++i) {
			if (path[i].className?.includes) {
				if ((typeof path[i].className === 'string'
					&& path[i].className.includes('month-popover-modal'))
					|| path[i].className.includes('event-container')
					|| path[i].className.includes('dialog-background')
					|| path[i].className.includes('dialog-container')) {
					keepOpen = true;
					break;
				}
			}
		}

		setPopIsOpen(keepOpen);
	}

	function closeExtraEventsPopover(e: any) {
		// if (!popIsOpen) return;
		const path = e.composedPath();
		const len = path.length;
		let keepOpen = false;

		for (let i = 0; i < len; ++i) {
			if (path[i].className?.includes) {
				if ((typeof path[i].className === 'string'
					&& path[i].className.includes('month-extra-events-modal'))
					|| path[i].className.includes('event-container')
					|| path[i].className.includes('dialog-background')
					|| path[i].className.includes('dialog-container')) {
					keepOpen = true;
					break;
				}
			}
		}

		setExtraEventsOpen(keepOpen);
	}

	const selectDateMenuOption = (option: SlidingMenuOption) => {
		if (calendarRef && calendarRef.current) {
			dispatch(setSelectedCalendarOption(option));
			const calendarApi = calendarRef.current.getApi();
			calendarApi.gotoDate(new Date(option.data.year, option.data.month, option.data.day));
			removeUnusedLeadingHours();
		}
	};

	const handleJoin = async (identifier: string) => {
		if (showDetailsButtonDisabled) return;

		const meeting = showSessions.find((ev: any) => (ev.meeting === identifier || ev.live_chat === identifier || ev.events_session_uuid === identifier) && ev.show_session_type_id === 0);

		if (meeting) {
			joinGreenroom(meeting);
		}
	};

	const handleSetUp = (identifier: string) => {
		if (showDetailsButtonDisabled) return;
		const meeting = showSessions.find((ev: any) => (ev.meeting === identifier || ev.live_chat === identifier || ev.events_session_uuid === identifier) && ev.show_session_type_id === 0);

		if (meeting) {
			handleEnterShowDetails(meeting);
		}
	};

	const extraEventPopoverClicked = (event: any) => {
		// currently not being used but going to keep it if we want to use it instead of the "expandEvents" below
		setExtraEventsOpen(false);

		setPopData({
			top: extraEventsData.top,
			left: extraEventsData.left,
			...event,
		});

		setPopIsOpen(true);
	};

	const expandEvents = () => {
		// this allows users to see all events for all days (normally it's 2/day)
		setMaxEventsADay(99);
	};

	const popDataToEventData = customDataToEventData(popData);

	return (<>
		<SlidingMenu
			options={slidingMenuOptions}
			callback={selectDateMenuOption}
			selectedOption={selectedCalendarOption}
		/>
		<div
			id="fc-calendar-plugin"
			className={classNames(currentCalendarType)}
		>
			<FullCalendar
				plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
				initialView={currentCalendarType}
				events={calendarEvents}
				eventContent={renderEventContent}
				headerToolbar={false}
				dateClick={dateClicked}
				eventClick={eventClicked}
				contentHeight="auto"
				ref={calendarRef}
				firstDay={1}
				allDaySlot={false}
				nowIndicator={true}
				slotEventOverlap={currentCalendarType !== 'timeGridDay'}
				dayMaxEvents={maxEventsADay}
				moreLinkClick={expandEvents}
				slotMinTime={timeGridStartTime}
				views={{
					dayGridMonth: {
						eventTimeFormat: {
							hour: 'numeric',
							minute: '2-digit',
							omitZeroMinute: true,
						},
					},
					timeGridWeek: {
						eventTimeFormat: {
							hour: 'numeric',
							minute: '2-digit',
							omitZeroMinute: false,
							meridiem: false,
						},
						eventMinHeight: 58,
					},
					timeGridDay: {
						eventTimeFormat: {
							hour: 'numeric',
							minute: '2-digit',
							omitZeroMinute: true,
							meridiem: false,
						},
						eventMinHeight: 60,
					},
				}}
				datesSet={(dateInfo: DatesSetArg) => setCurrentCalendarType(dateInfo.view.type)}
			/>
		</div>
		<div
			className={classNames('month-popover-modal', { open: popIsOpen })}
			style={{ top: popData.top, left: popData.left }}
			ref={popoverRef}
		>
			<div className="month-popover-modal-image" style={popData.backgroundStyle}></div>
			<div className="month-popover-modal-top">
				<Badges
					start_time={popData.start_time}
					end_time={popData.end_time}
					show_session_type={popData.show_session_type}
				/>
				<div className="card-presenters">
					<AvatarBubbleRow invites={popData.invites} size={'xsmall'} alignEnd={true} />
					{popDataToEventData && (
						<EventMenu event={popDataToEventData} />
					)}
				</div>
			</div>
			<div className="month-popover-modal-bottom">
				<div className="month-popover-modal-time">{popData.time}</div>
				<div className="month-popover-modal-info">{popData.title}</div>
				<div className="month-popover-modal-buttons">
					{!awaitingProducer ? (
						<>
							{popData.isSetUp && <div className="month-popover-button join" onClick={() => handleJoin(popData.identifier)}>
								<span>Join Greenroom</span>
							</div>}
							<div className="month-popover-button setup" onClick={() => handleSetUp(popData.identifier)}>
								<span>Set up show</span>
							</div>
						</>
					) : (
						<div className="join-waiting">
							<span>You'll join when the producer lets you in </span><WaitingIndicator />
						</div>
					)}
				</div>
			</div>
		</div>
		<div
			className={classNames('month-extra-events-modal', { open: extraEventsOpen })}
			style={{ top: extraEventsData.top, left: extraEventsData.left }}
		>
			<div className="extra-ev-modal-day">{extraEventsData.dateRow}</div>

			{extraEventsData.events.map((meetingEvent: any) => <div className="month-extra-events-modal-event-row" onClick={() => extraEventPopoverClicked(meetingEvent)}>
					<div className={classNames('event-color-indicator ', meetingEvent.color)}></div>
					<span className="extra-ev-date">{new Date(meetingEvent.start_time).toLocaleString([], { hour: 'numeric', minute: '2-digit' })}</span>
					<span> &nbsp; {meetingEvent.title}</span>
				</div>)}
		</div>
	</>);
}
